import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fmtMemberId } from '@lib/member-id';
import { resultChipClasses } from '@lib/config-render';
import { groupIntoBatches, resultMix, type BatchGroup } from '@lib/archive-batches';
import { useArchiveQueue } from '@hooks/useArchiveQueue';
import { useIntakeConfig } from '@hooks/useIntakeConfig';
import { ReviewPhoto } from '@components/ReviewPhoto';
import { ReviewVoice } from '@components/ReviewVoice';
import type { IntakeConfig, SidecarRecord } from '@/types/intake';

import { useCampaign } from '@hooks/useCampaign';

/**
 * Archive: read-only history of records that were sent through a Send It / Send All batch (or the
 * legacy OK-to-print dispatch). A record leaves the active Review worklist the moment it is sent and
 * lives here permanently, backed by the durable Arc batch folders. Records are grouped into the send
 * batch ("approval stack") they went out in; each batch header expands to reveal its 3-across card
 * grid. Every document is retained - the sidecar JSON, the device photos, and any voice notes stay in
 * SharePoint and are never deleted - so the archive can always re-open the full record and its media.
 * A search box filters by member name / member id / kit barcode / record id / return tracking; when a
 * query is active, matching batches are auto-expanded. There are NO mutation actions here.
 */
export function Archive() {
  const { campaign: CAMPAIGN } = useCampaign();
  const qc = useQueryClient();
  const cfg = useIntakeConfig(CAMPAIGN);
  const list = useArchiveQueue(CAMPAIGN);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const refresh = () => void qc.invalidateQueries({ queryKey: ['intake-archive', CAMPAIGN] });

  const items = list.data?.items ?? [];
  const needle = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!needle) return items;
    return items.filter((r) =>
      [r.memberNameSnapshot, fmtMemberId(r.memberId), r.memberId, r.kitBarcode, r.recordId, r.returnTracking]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle)),
    );
  }, [items, needle]);

  // Filter first, then group: a search shows only batches that contain a match.
  const batches = useMemo(() => groupIntoBatches(filtered), [filtered]);

  const toggle = (batchId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });

  if (cfg.isLoading || !cfg.data)
    return (
      <div className="li-page">
        <div className="li-state">Loading...</div>
      </div>
    );
  const config = cfg.data;

  return (
    <div className="li-page">
      <div className="li-head flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-eyebrow">Records - 26 FOBT</div>
          <h2 className="section-title">Archive</h2>
          <p className="mt-1 text-xs tab-num" style={{ color: 'var(--ink-400)' }}>
            {items.length} record{items.length === 1 ? '' : 's'} sent
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={list.isFetching}
          className="btn btn-ghost btn-sm disabled:opacity-50"
        >
          {list.isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search name, member ID, kit, or tracking"
        className="text-input mb-4 w-full"
      />
      {list.isLoading && <p className="text-sm" style={{ color: 'var(--ink-400)' }}>Loading archive...</p>}
      {!list.isLoading && items.length === 0 && <div className="li-state">Nothing has been sent yet.</div>}
      {!list.isLoading && items.length > 0 && filtered.length === 0 && (
        <p className="text-sm" style={{ color: 'var(--ink-400)' }}>No records match &ldquo;{q}&rdquo;.</p>
      )}
      <div className="space-y-3">
        {batches.map((batch) => (
          <BatchStack
            key={batch.batchId}
            batch={batch}
            config={config}
            // While searching, every shown batch is auto-expanded so matches are visible.
            open={needle ? true : expanded.has(batch.batchId)}
            onToggle={() => toggle(batch.batchId)}
          />
        ))}
      </div>
    </div>
  );
}

/** A collapsible batch header (the "approval stack") over a 3-across grid of its archived cards. */
function BatchStack({
  batch,
  config,
  open,
  onToggle,
}: {
  batch: BatchGroup;
  config: IntakeConfig;
  open: boolean;
  onToggle: () => void;
}) {
  const count = batch.records.length;
  const mix = resultMix(batch.records);
  const label = batch.isLegacy
    ? batch.latestSentAt
      ? `Earlier (pre-batch) - ${batch.latestSentAt}`
      : 'Earlier (pre-batch)'
    : batch.batchId;
  return (
    <div className="card card-flush overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="li-list-row"
        style={{ padding: '12px 16px' }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
            style={{ color: 'var(--ink-400)' }}
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
              clipRule="evenodd"
            />
          </svg>
          <span className="truncate font-mono text-sm font-semibold text-cdx-fg-strong">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-xs" style={{ color: 'var(--ink-400)' }}>
          {batch.sender && (
            <span className="font-medium" style={{ color: 'var(--ink-600)' }}>
              {batch.sender}
            </span>
          )}
          {mix && <span>{mix}</span>}
          <span className="pill tab-num">
            {count} record{count === 1 ? '' : 's'}
          </span>
        </div>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--rule-light)', padding: 12 }}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {batch.records.map((r) => (
              <ArchiveCard key={r.recordId} record={r} config={config} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** A static "Sent" pill with a checkmark - every archived card carries it (spec 2.1 / 2.4). */
function SentBadge() {
  return (
    <span className="pill pill-success shrink-0">
      <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.5 7.57a1 1 0 0 1-1.42 0l-3.5-3.535a1 1 0 1 1 1.42-1.408l2.79 2.817 6.79-6.855a1 1 0 0 1 1.414-.006Z"
          clipRule="evenodd"
        />
      </svg>
      Sent
    </span>
  );
}

function ArchiveCard({ record, config }: { record: SidecarRecord; config: IntakeConfig }) {
  const { campaign: CAMPAIGN } = useCampaign();
  const sent = record.sent;
  const legacy = record.printDispatch;
  // Sent date: prefer the new Send batch (the Arc batch date), fall back to the legacy OK-to-print stamp.
  const sentDate = sent?.sentAt ?? legacy?.dispatchedAt ?? null;
  return (
    <div className="card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-cdx-fg-strong">{record.memberNameSnapshot || '(no name)'}</div>
          <div className="text-xs" style={{ color: 'var(--ink-400)' }}>
            Member {fmtMemberId(record.memberId)} - kit <span className="font-mono">{record.kitBarcode}</span>
          </div>
        </div>
        <SentBadge />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className={resultChipClasses(config, record.result)}>{record.result}</span>
        {sentDate && (
          <span className="text-xs" style={{ color: 'var(--ink-400)' }}>
            Sent {sentDate}
            {sent?.batchId && (
              <>
                {' '}
                in batch <span className="font-mono">{sent.batchId}</span>
              </>
            )}
          </span>
        )}
      </div>

      <div className="mt-2 space-y-0.5 text-xs" style={{ color: 'var(--ink-600)' }}>
        <div>
          Resulted {record.resultedAt} by {record.resultedByEmail ?? record.resultedBy.slice(0, 8)}
        </div>
        {record.collectionDate && <div>Collection date: {record.collectionDate}</div>}
        {record.reason && <div>Reason: {record.reason}</div>}
        {record.status === 'applied' && record.appliedAt && (
          <div style={{ color: 'var(--emerald)' }}>Live since {record.appliedAt}</div>
        )}
        {sent && (
          <div style={{ color: 'var(--teal-2)' }}>
            Sent {sent.sentAt} by {sent.sentByName ?? sent.sentByEmail ?? sent.sentBy.slice(0, 8)} - batch{' '}
            <span className="font-mono">{sent.batchId}</span>
          </div>
        )}
        {!sent && legacy && (
          <div style={{ color: 'var(--teal-2)' }}>
            Sent to print {legacy.dispatchedAt} by {legacy.dispatchedBy.slice(0, 8)} - job{' '}
            <span className="font-mono">{legacy.jobId}</span>
          </div>
        )}
      </div>

      {record.photos.length > 0 && (
        <div className="mt-3">
          <div className="micro" style={{ marginBottom: 4 }}>Photos ({record.photos.length})</div>
          <div className="flex flex-wrap gap-2">
            {record.photos.map((p, i) => (
              <ReviewPhoto key={p} campaign={CAMPAIGN} recordId={record.recordId} index={i} />
            ))}
          </div>
        </div>
      )}

      {record.voiceNotes && record.voiceNotes.length > 0 && (
        <div className="mt-3">
          <div className="micro" style={{ marginBottom: 4 }}>Voice notes ({record.voiceNotes.length})</div>
          <div className="space-y-2">
            {record.voiceNotes.map((p, i) => (
              <ReviewVoice key={p} campaign={CAMPAIGN} recordId={record.recordId} index={i} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
